import { DownloadOutlined, LoadingOutlined } from "@ant-design/icons";
import type { IntlShape } from "@formatjs/intl";
import { Button, Flex, Image, Modal, Spin, Typography } from "antd";
import { type ReactNode, useCallback, useEffect, useState } from "react";
import {
  getFileTypeIcon,
  isBrowserRenderableImageContentType,
  isPdfContentType,
} from "../utils/getFileTypeIcon";

const PDF_PREVIEW_HEIGHT = "75vh";

export type PreviewableFile = {
  id?: string;
  content_type?: string | null;
  name?: string | null;
};

export function useFilePreview() {
  const [previewFile, setPreviewFile] = useState<PreviewableFile | null>(null);

  const openPreview = useCallback((file: PreviewableFile) => setPreviewFile(file), []);
  const closePreview = useCallback(() => setPreviewFile(null), []);

  return { previewFile, openPreview, closePreview };
}

function PdfPreviewFrame({
  url,
  name,
  fallback,
}: {
  url: string;
  name: string;
  fallback: ReactNode;
}) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [hasFailed, setHasFailed] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    let objectUrl: string | undefined;
    setBlobUrl(null);
    setHasFailed(false);

    fetch(url, { signal: controller.signal })
      .then((response) => {
        const contentType = response.headers.get("content-type") ?? "";
        if (!response.ok || !contentType.includes("application/pdf")) {
          throw new Error(`Failed to load PDF: ${response.status} ${contentType}`);
        }
        return response.blob();
      })
      .then((blob) => {
        objectUrl = URL.createObjectURL(blob);
        setBlobUrl(objectUrl);
      })
      .catch((error: Error) => {
        if (error.name !== "AbortError") {
          setHasFailed(true);
        }
      });

    return () => {
      controller.abort();
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [url]);

  if (hasFailed) {
    return <>{fallback}</>;
  }

  if (!blobUrl) {
    return (
      <Flex justify="center" align="center" style={{ height: PDF_PREVIEW_HEIGHT }}>
        <Spin indicator={<LoadingOutlined spin />} size="large" />
      </Flex>
    );
  }

  return (
    <iframe
      src={blobUrl}
      title={name}
      style={{ width: "100%", height: PDF_PREVIEW_HEIGHT, border: 0 }}
    />
  );
}

type FilePreviewModalProps = {
  file: PreviewableFile | null;
  onClose: () => void;
  getDownloadUrl: (file: PreviewableFile) => string;
  intl: IntlShape;
};

export function FilePreviewModal({
  file,
  onClose,
  getDownloadUrl,
  intl,
}: FilePreviewModalProps) {
  if (!file) return null;

  const isImage = isBrowserRenderableImageContentType(file.content_type);
  const isPdf = isPdfContentType(file.content_type);
  const fileUrl = file.id ? getDownloadUrl(file) : undefined;

  if (isImage && fileUrl) {
    return (
      <Image
        src={fileUrl}
        width={0}
        height={0}
        style={{ display: "none" }}
        preview={{
          visible: true,
          src: fileUrl,
          onVisibleChange: (visible) => {
            if (!visible) onClose();
          },
        }}
      />
    );
  }

  const fallbackContent = (
    <Flex vertical align="center" gap={12} style={{ padding: "32px 0" }}>
      <span style={{ fontSize: 48, color: "var(--ant-color-text-secondary)" }}>
        {getFileTypeIcon({ contentType: file.content_type, name: file.name })}
      </span>
      <Typography.Text type="secondary">
        {intl.formatMessage({ id: "ticket_file_preview_unavailable" })}
      </Typography.Text>
    </Flex>
  );

  return (
    <Modal
      open
      onCancel={onClose}
      title={file.name}
      centered
      width="min(90vw, 1000px)"
      footer={[
        <Typography.Link
          key="download"
          href={getDownloadUrl(file)}
          download={file.name ?? true}
        >
          <Button type="primary" icon={<DownloadOutlined />}>
            {intl.formatMessage({ id: "ticket_file_download" })}
          </Button>
        </Typography.Link>,
      ]}
    >
      {isPdf && fileUrl ? (
        <PdfPreviewFrame
          url={fileUrl}
          name={file.name ?? "preview"}
          fallback={fallbackContent}
        />
      ) : (
        fallbackContent
      )}
    </Modal>
  );
}
